import { createAppError } from '../../api/errors';
import { createRepositoryToken } from '../../data/RepositoryProvider';
import { transactionFixtures } from './fixtures';
import type { TransactionRepository } from './repository';
import type { TransactionListQuery, TransactionSummary } from './types';
import { bangkokWallTimeToRfc3339 } from './types';

// Stable, human-readable route identifiers resolve to the first fixture without duplicating
// fixture rows or altering list/pagination totals.
const transactionDetailAliases: Readonly<Record<string, string>> = {
  'tx-a': transactionFixtures[0]!.id,
};

export class MockTransactionRepository implements TransactionRepository {
  constructor(private readonly mode: 'success' | 'empty' | 'error' = 'success') {}

  async list(query: TransactionListQuery, signal: AbortSignal) {
    abort(signal);
    if (this.mode === 'error') throw mockError('MOCK_TRANSACTION_LIST_FAILED');
    const items =
      this.mode === 'empty' ? [] : transactionFixtures.filter((item) => matches(item, query));
    const ordered = [...items].sort(
      (left, right) =>
        Date.parse(right[query.sort]) - Date.parse(left[query.sort]) ||
        right.id.localeCompare(left.id),
    );
    const offset = cursorOffset(query.cursor);
    const page = ordered.slice(offset, offset + query.pageSize);
    return {
      items: page,
      nextCursor:
        offset + query.pageSize < ordered.length ? `offset:${offset + query.pageSize}` : null,
      requestId: 'mock-transaction-list-request',
    };
  }

  async get(transactionId: string, signal: AbortSignal) {
    abort(signal);
    if (this.mode === 'error') throw mockError('MOCK_TRANSACTION_DETAIL_FAILED');
    const resolvedId = transactionDetailAliases[transactionId] ?? transactionId;
    const item = transactionFixtures.find((candidate) => candidate.id === resolvedId);
    if (item === undefined) throw mockError('TRANSACTION_NOT_FOUND');
    return item;
  }
}

export interface TransactionFailureQueueItem {
  readonly id: string;
  readonly errorCode: string;
  readonly fieldErrors: readonly string[];
  readonly partner: string;
  readonly firstOccurredAt: string;
  readonly lastOccurredAt: string;
  readonly retryCount: number;
  readonly supportNotes: string;
  readonly requestId: string;
}

export interface MockTransactionReversalInput {
  readonly transactionId: string;
  readonly persistedStatus: string;
  readonly reversalState: string;
  readonly reason: string;
  readonly signedImpact: string;
  readonly currencyCode: string;
}

export interface MockTransactionReversalApproval {
  readonly approvalId: string;
  readonly status: 'PENDING_MOCK_APPROVAL';
}

export interface TransactionGapRepository {
  listFailures(signal: AbortSignal): Promise<readonly TransactionFailureQueueItem[]>;
  submitReversal(input: MockTransactionReversalInput): Promise<MockTransactionReversalApproval>;
}

export const transactionGapRepositoryToken =
  createRepositoryToken<TransactionGapRepository>('transactionGaps');

const mockFailureQueue = Object.freeze<readonly TransactionFailureQueueItem[]>([
  {
    id: 'mock-failure-normalization',
    errorCode: 'NORMALIZATION_FAILED',
    fieldErrors: ['gameCode: UNKNOWN_GAME'],
    partner: 'Alpha Partner',
    firstOccurredAt: '2026-07-22T08:05:00+07:00',
    lastOccurredAt: '2026-07-22T10:30:00+07:00',
    retryCount: 3,
    supportNotes: 'Partner mapping needs investigation.',
    requestId: 'mock-req-failure-001',
  },
  {
    id: 'mock-failure-member',
    errorCode: 'MEMBER_RESOLUTION_FAILED',
    fieldErrors: ['username: MEMBER_NOT_FOUND'],
    partner: 'Beta Partner',
    firstOccurredAt: '2026-07-22T09:00:00+07:00',
    lastOccurredAt: '2026-07-22T09:00:00+07:00',
    retryCount: 0,
    supportNotes: 'Awaiting Partner identity confirmation.',
    requestId: 'mock-req-failure-002',
  },
]);

export class MockTransactionGapRepository implements TransactionGapRepository {
  async listFailures(signal: AbortSignal) {
    abort(signal);
    return mockFailureQueue;
  }

  async submitReversal(input: MockTransactionReversalInput) {
    if (
      input.persistedStatus !== 'CALCULATED' ||
      input.reversalState !== 'NOT_REVERSED' ||
      input.reason.trim().length === 0
    ) {
      throw mockError('MOCK_REVERSAL_NOT_ELIGIBLE');
    }
    return {
      approvalId: `MOCK-REVERSAL-APPROVAL-${input.transactionId.slice(-6).toUpperCase()}`,
      status: 'PENDING_MOCK_APPROVAL' as const,
    };
  }
}

function matches(item: TransactionSummary, query: TransactionListQuery): boolean {
  const f = query.filters;
  return (
    exact(item.partner.id, f.partnerId) &&
    exact(item.resolution?.agentId ?? null, f.agentId) &&
    exact(item.resolution?.memberId ?? null, f.memberId) &&
    exact(item.submittedUsername, f.username) &&
    exact(item.submittedPartnerUserId, f.partnerUserId) &&
    exact(item.resolution?.uplineMemberId ?? null, f.uplineMemberId) &&
    exact(item.resolution?.uplineUsername ?? null, f.uplineUsername) &&
    exact(mockGameTypeIds[item.id] ?? null, f.gameTypeId) &&
    exact(item.submittedGameTypeCode, f.gameTypeCode.toUpperCase()) &&
    exact(item.resolution?.gameId ?? null, f.gameId) &&
    exact(item.submittedGameCode, f.gameCode.toUpperCase()) &&
    exact(item.gameRound, f.gameRound) &&
    exact(item.partnerTransactionId, f.partnerTransactionId) &&
    (f.outcome === '' || item.outcome === f.outcome) &&
    (f.status === '' || item.persistedStatus === f.status) &&
    (f.reversalState === '' || item.reversalState === f.reversalState) &&
    (f.currency === '' || item.currencyCode === f.currency.toUpperCase()) &&
    exact(item.failure?.code ?? null, f.errorCode.toUpperCase()) &&
    (f.minimumAmount === '' || compareDecimals(item.actualPlayAmount, f.minimumAmount) >= 0) &&
    (f.maximumAmount === '' || compareDecimals(item.actualPlayAmount, f.maximumAmount) <= 0) &&
    within(item.playAt, f.playFrom, f.playTo) &&
    within(item.receivedAt, f.receivedFrom, f.receivedTo)
  );
}

const mockGameTypeIds: Readonly<Record<string, string>> = {
  '00000000-0000-4000-8000-000000000401': '00000000-0000-4000-8000-000000000901',
  '00000000-0000-4000-8000-000000000402': '00000000-0000-4000-8000-000000000901',
};

function compareDecimals(left: string, right: string): number {
  const leftParts = decimalParts(left);
  const rightParts = decimalParts(right);
  const scale = Math.max(leftParts.fraction.length, rightParts.fraction.length);
  const leftUnits = signedUnits(leftParts, scale);
  const rightUnits = signedUnits(rightParts, scale);
  return leftUnits < rightUnits ? -1 : leftUnits > rightUnits ? 1 : 0;
}

function decimalParts(value: string) {
  const match = /^(-)?(0|[1-9][0-9]*)(?:\.([0-9]+))?$/u.exec(value.trim());
  if (match === null) throw new Error('Expected a canonical decimal string');
  return { negative: match[1] === '-', whole: match[2] ?? '0', fraction: match[3] ?? '' };
}

function signedUnits(parts: ReturnType<typeof decimalParts>, scale: number) {
  const units =
    BigInt(parts.whole) * 10n ** BigInt(scale) + BigInt(parts.fraction.padEnd(scale, '0') || '0');
  return parts.negative ? -units : units;
}

function exact(value: string | null, filter: string) {
  return filter.trim() === '' || value === filter.trim();
}
function within(value: string, from: string, to: string) {
  const instant = Date.parse(value);
  return (
    (from === '' || instant >= Date.parse(bangkokWallTimeToRfc3339(from))) &&
    (to === '' || instant < Date.parse(bangkokWallTimeToRfc3339(to)))
  );
}
function cursorOffset(cursor: string) {
  const match = /^offset:(\d+)$/u.exec(cursor);
  return match === null ? 0 : Number(match[1]);
}
function abort(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
}
function mockError(code: string) {
  return createAppError({
    status: code === 'TRANSACTION_NOT_FOUND' ? 404 : 500,
    code,
    message: code.replaceAll('_', ' '),
    requestId: 'mock-transaction-request',
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}
